import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Offer } from '@app/quoting/models/quote';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { MoreDetails } from '../../models/more-details';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { tap } from 'rxjs/operators';
import { ClearConsumptionData, LoadConsumptionData } from '@utility-information/state/actions/utility-information.actions';
import { consumptionDataSelector } from '@utility-information/state/selectors/utility-information.selectors';



export enum MoreDetailsToggleOptions {
  TotalCost = 'TotalCost',
  PerUnit = 'PerUnit',
  Commission = 'Commission',
}

export interface MonthlyConsumptionData {
  Month: string;
  EstimatedConsumptionkWh: number;
  EstimatedCommissionDollars: number;
  ActualConsumptionkWh: number;
  ActualCommissionDollars: number;
}

@Component({
  selector: 'app-more-details',
  templateUrl: './more-details.component.html',
  styleUrls: ['./more-details.component.scss'],
})
export class MoreDetailsComponent implements OnInit {
  public commissionStatsLoading = true;

  displayTotals = true;

  moreDetails: MoreDetails[] = [];
  dataSourceCostAndRate = new MatTableDataSource<MoreDetails>(this.moreDetails);
  dataSourceCommission = new MatTableDataSource<MonthlyConsumptionData>();
  displayedColumns = [];
  displayedColumnsCosts = [
    'utilityAccountNumber',
    'energyCostTotal',
    'capacityCostTotal',
    'nitsCostTotal',
    'otherIsoCostsTotal',
    'tduCostTotal',
    'rpsCostTotal',
    'otherCostsTotal',
    'totalTotal',
  ];
  displayedColumnsCommission = [
    'month',
    'estimatedConsumptionkWh',
    'estimatedCommissionDollars',
    'actualConsumptionkWh',
    'actualCommissionDollars',
  ];
  term: any;
  public displayCommission: boolean;
  dataSet: MoreDetailsToggleOptions;
  startDate: any;
  uplift: any;

  constructor(
    public dialogRef: MatDialogRef<MoreDetailsComponent>,
    private quoteRequestService: QuoteRequestService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    this.moreDetails = this.getMoreDetailsFromQuote(this.data.offer);
    this.displayedColumns = this.displayedColumnsCosts;
    this.dataSet = MoreDetailsToggleOptions.TotalCost;
    this.dataSourceCostAndRate = new MatTableDataSource<MoreDetails>(this.moreDetails);
    this.term = this.data.term;
    this.uplift = this.data.uplift;
    this.startDate = this.data.startDate;

    this.store.dispatch(new ClearConsumptionData(this.data.quoteRequestId));
    this.store.dispatch(new LoadConsumptionData(this.data.quoteRequestId, this.term, this.uplift));
    this.store.select(consumptionDataSelector)
    .pipe(
      tap(x => this.commissionStatsLoading = false),
      tap(x =>  this.dataSourceCommission = new MatTableDataSource<MonthlyConsumptionData>(this.getConsumptionDataModel(x)))
    ).subscribe();
  }

  private getMoreDetailsFromQuote(quote: Offer): MoreDetails[] {
    const totalConsumption = quote.details.reduce((sum, current) => sum + current.volumeTotal, 0);

    const md: MoreDetails[] = [];
    quote.details.forEach((x) => {
      md.push(
        new MoreDetails(
          x.utilityAccountNumber,
          totalConsumption,
          x.volumeTotal,
          x.energyCostTotal,
          x.capacityCostTotal,
          x.nitsCostTotal,
          x.ancillaryCostTotal,
          x.tduCostTotal,
          x.recComplianceCostTotal,
          x.brokerCommissionTotal,
          x.revenueTotal,
          x.upliftPerUnit + x.marginPerUnit,
          x.clientAdderTotal,
          x.nonIsoEnergyCostTotal
        )
      );
    });
    return md;
  }

  setTotals() {
    this.dataSet = MoreDetailsToggleOptions.TotalCost;
  }

  setUnitRate() {
    this.dataSet = MoreDetailsToggleOptions.PerUnit;
  }

  setCommission() {
    this.dataSet = MoreDetailsToggleOptions.Commission;
  }

  public showCommissionData() {
    return this.dataSet === MoreDetailsToggleOptions.Commission;
  }

  public showTotalCostData() {
    return this.dataSet === MoreDetailsToggleOptions.TotalCost;
  }

  public showPerUnitData() {
    return this.dataSet === MoreDetailsToggleOptions.PerUnit;
  }

  public getTotalEstimatedCommission() {
    let sum = 0;
    this.dataSourceCommission.data.forEach((x) => (sum = sum + x.EstimatedCommissionDollars));
    return sum;
  }

  public getTotalActualCommission() {
    let sum = 0;
    this.dataSourceCommission.data.forEach((x) => (sum = sum + x.ActualCommissionDollars));
    return sum;
  }

  getModifier() {
    return Math.random() / 5 + 0.9;
  }

  getConsumptionDataModel(consumptionData: any[]) {
    const startDate = new Date(this.startDate);
    // US Eastern Standard Time resolves first of month to 31 of previous month so add a day
    startDate.setDate(startDate.getDate() + 1);
    const months: MonthlyConsumptionData[] = [];

    consumptionData?.forEach(data => {
      months.push({
        Month: data.shortMonth,
        EstimatedConsumptionkWh: data.consumption,
        EstimatedCommissionDollars: data.commission,
        ActualConsumptionkWh: null,
        ActualCommissionDollars: null,
      });
    });

    return months; // Months should be in ascending order (do not add the reverse), from JC.  The contradicts SALES360-111 but that's the requirement. //.reverse();
  }

  getTotalCost() {
    const totalTotal = this.moreDetails?.map((t) => t.totalTotal).reduce((acc, value) => acc + value, 0);
    if (this.showTotalCostData()) {
      return totalTotal;
    }

    return (totalTotal / this.moreDetails[0].totalConsumption) * 1000;
  }

  isDefined(value: any) {
    return value !== undefined && value !== null;
  }
}
